import type { DataGridProProps, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Button } from '@tempo-io/sui-button';
import { DoNotDisturbOn } from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@tempo-io/sui-tooltip';
import type { TFunction } from 'i18next';
import type { ItemHealth } from '@/common/api/types/dashboard/health';
import type { ItemStatus } from '@/common/api/types/itemStatus';
import type { ItemType } from '@/common/api/types/itemType';
import type { Team } from '@/common/api/types/teams';
import { formatLocalDate } from '@/common/utils/dateFormats';
import { DependencyNameCell } from '@/features/item-panel/components/tabs/dependencies/DependencyNameCell';
import { DependencyPredecessorCell } from '@/features/item-panel/components/tabs/dependencies/DependencyPredecessorCell';
import { DependencySuccessorCell } from '@/features/item-panel/components/tabs/dependencies/DependencySuccessorCell';
import { MIN_NAME_COLUMN_WIDTH } from '@/features/item-panel/components/tabs/dependencies/lib/constants';
import {
  type DependencyImpactState,
  SEVERITY_TEXT_COLOR,
} from '@/features/item-panel/components/tabs/dependencies/lib/dependencyImpact';
import {
  resolvePredecessorImpactState,
  resolveSuccessorImpactState,
} from '@/features/item-panel/components/tabs/dependencies/lib/edgeImpact';
import type {
  DependencyRow,
  DependencySectionValue,
} from '@/features/item-panel/components/tabs/dependencies/lib/types';
import { StatusCell } from '@/features/item-panel/components/tabs/dependencies/StatusCell';
import { TeamDisplayCell } from '@/features/items/components/table/cells/TeamDisplayCell';
import type { ItemsTableRow } from '@/features/items/components/table/types';

const DATE_COLOR_BY_IMPACT_STATE: Record<DependencyImpactState, string | undefined> = {
  error: SEVERITY_TEXT_COLOR.error,
  warning: SEVERITY_TEXT_COLOR.warning,
  neutral: undefined,
  none: undefined,
};

export const buildGroupingColDef = (
  nameHeader: string,
  itemTypes: ItemType[],
): DataGridProProps<ItemsTableRow>['groupingColDef'] => ({
  headerName: nameHeader,
  valueGetter: (_value, row: ItemsTableRow) => row.name,
  renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
    <DependencyNameCell row={params.row} itemTypes={itemTypes} />
  ),
  minWidth: MIN_NAME_COLUMN_WIDTH,
  flex: 1,
  hideDescendantCount: true,
  hideable: false,
  sortable: true,
  disableColumnMenu: false,
  pinnable: false,
});

interface BuildColumnsOptions {
  section: DependencySectionValue;
  currentItemId: number;
  currentItemStatusId: number | null | undefined;
  currentItemHealth: ItemHealth | undefined;
  healthByItemId: Map<number, ItemHealth | undefined>;
  dependencyRulesV2: boolean;
  itemStatuses: ItemStatus[];
  anchorDate: string | null | undefined;
  canRemove: boolean;
  isRemoveInFlight: boolean;
  showForecastedDates: boolean;
  inheritedItemIds: ReadonlySet<number>;
  onRemove: (section: DependencySectionValue, rowItemId: number) => void;
  teamsById: Record<Team['id'], Team>;
  t: TFunction;
}

const buildDependencyStatusColumn = ({
  section,
  currentItemId,
  currentItemStatusId,
  currentItemHealth,
  healthByItemId,
  dependencyRulesV2,
  itemStatuses,
  anchorDate,
  t,
}: BuildColumnsOptions): GridColDef<DependencyRow> => {
  return {
    field: 'dependencyStatus',
    headerName: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.impact),
    width: 200,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams<DependencyRow>) =>
      section === 'predecessors' ? (
        <DependencyPredecessorCell
          row={params.row}
          currentItemHealth={currentItemHealth}
          dependencyRulesV2={dependencyRulesV2}
          itemStatuses={itemStatuses}
          anchorDate={anchorDate}
        />
      ) : (
        <DependencySuccessorCell
          row={params.row}
          currentItemId={currentItemId}
          currentItemStatusId={currentItemStatusId}
          currentItemHealth={currentItemHealth}
          healthByItemId={healthByItemId}
          dependencyRulesV2={dependencyRulesV2}
          itemStatuses={itemStatuses}
          anchorDate={anchorDate}
        />
      ),
  };
};

const buildStatusColumn = ({
  itemStatuses,
  t,
}: BuildColumnsOptions): GridColDef<ItemsTableRow> => ({
  field: 'status',
  headerName: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.status),
  width: 140,
  sortable: true,
  valueGetter: (_value, row: ItemsTableRow) =>
    itemStatuses.find((itemStatus) => itemStatus.id === row.itemStatusId)?.priority ?? '',
  renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
    <StatusCell row={params.row} itemStatuses={itemStatuses} />
  ),
});

const buildTeamColumn = ({ teamsById, t }: BuildColumnsOptions): GridColDef<ItemsTableRow> => ({
  field: 'team',
  headerName: t(($) => $.views.table.columns.systemFields.team),
  width: 140,
  sortable: true,
  valueGetter: (_value, row: ItemsTableRow) =>
    row.teamId ? (teamsById[row.teamId]?.name ?? '') : '',
  renderCell: TeamDisplayCell,
});

const buildPlannedDateColumn = ({
  section,
  currentItemId,
  currentItemStatusId,
  currentItemHealth,
  healthByItemId,
  dependencyRulesV2,
  itemStatuses,
  anchorDate,
  t,
}: BuildColumnsOptions): GridColDef<DependencyRow> => {
  return section === 'successors'
    ? {
        field: 'plannedStart',
        headerName: t(($) => $.views.table.columns.systemFields.plannedStartDate),
        width: 140,
        sortable: true,
        valueGetter: (_value, row: DependencyRow) => row.plannedStartDateTime ?? '',
        renderCell: (params: GridRenderCellParams<DependencyRow>) => {
          if (!params.row.plannedStartDateTime) {
            return '-';
          }
          const impactState = resolveSuccessorImpactState(dependencyRulesV2, {
            row: params.row,
            currentItemId,
            currentItemStatusId,
            currentItemHealth,
            healthByItemId,
            itemStatuses,
            anchorFinishDate: anchorDate,
          });
          return (
            <span className={DATE_COLOR_BY_IMPACT_STATE[impactState]} data-severity={impactState}>
              {formatLocalDate(params.row.plannedStartDateTime)}
            </span>
          );
        },
      }
    : {
        field: 'plannedFinish',
        headerName: t(($) => $.views.table.columns.systemFields.plannedFinishDate),
        width: 140,
        sortable: true,
        valueGetter: (_value, row: DependencyRow) => row.plannedFinishDateTime ?? '',
        renderCell: (params: GridRenderCellParams<DependencyRow>) =>
          params.row.plannedFinishDateTime
            ? formatLocalDate(params.row.plannedFinishDateTime)
            : '-',
      };
};

const buildForecastedDateColumn = ({
  section,
  currentItemHealth,
  dependencyRulesV2,
  itemStatuses,
  anchorDate,
  t,
}: BuildColumnsOptions): GridColDef<DependencyRow> => {
  return section === 'successors'
    ? {
        field: 'forecastedStart',
        headerName: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.forecastedStart),
        width: 170,
        sortable: true,
        valueGetter: (_value, row: DependencyRow) => row.forecastedStartDateTime ?? '',
        renderCell: (params: GridRenderCellParams<DependencyRow>) =>
          params.row.forecastedStartDateTime
            ? formatLocalDate(params.row.forecastedStartDateTime)
            : '-',
      }
    : {
        field: 'forecastedFinish',
        headerName: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.forecastedFinish),
        width: 170,
        sortable: true,
        valueGetter: (_value, row: DependencyRow) => row.forecastedFinishDateTime ?? '',
        renderCell: (params: GridRenderCellParams<DependencyRow>) => {
          if (!params.row.forecastedFinishDateTime) {
            return '-';
          }
          const impactState = resolvePredecessorImpactState(dependencyRulesV2, {
            row: params.row,
            currentItemHealth,
            itemStatuses,
            anchorStartDate: anchorDate,
          });
          return (
            <span className={DATE_COLOR_BY_IMPACT_STATE[impactState]} data-severity={impactState}>
              {formatLocalDate(params.row.forecastedFinishDateTime)}
            </span>
          );
        },
      };
};

const buildRemoveColumn = ({
  section,
  isRemoveInFlight,
  inheritedItemIds,
  onRemove,
  t,
}: BuildColumnsOptions): GridColDef<ItemsTableRow> => ({
  field: 'actions',
  headerName: '',
  width: 48,
  sortable: false,
  disableColumnMenu: true,
  renderCell: (params: GridRenderCellParams<ItemsTableRow>) => {
    const isInherited = inheritedItemIds.has(params.row.id);
    const isDisabled = isInherited || isRemoveInFlight;
    return (
      <div className="flex justify-end pr-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!isInherited) {
                      onRemove(section, params.row.id);
                    }
                  }}
                  disabled={isDisabled}
                  aria-label={t(
                    ($) =>
                      $.adaptivePlanner.itemPanel.dependenciesSection.removeDependencyAriaLabel,
                    { itemName: params.row.name ?? '' },
                  )}
                  className="shrink-0 text-text-neutral-secondary"
                >
                  <DoNotDisturbOn />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {isInherited
                ? t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.cannotRemoveInherited)
                : t(($) => $.common.remove)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  },
});

export const buildColumns = (options: BuildColumnsOptions): GridColDef<DependencyRow>[] => {
  const columns: GridColDef<DependencyRow>[] = [
    buildDependencyStatusColumn(options),
    buildStatusColumn(options),
    buildTeamColumn(options),
    buildPlannedDateColumn(options),
  ];
  if (options.showForecastedDates) {
    columns.push(buildForecastedDateColumn(options));
  }
  if (options.canRemove) {
    columns.push(buildRemoveColumn(options));
  }
  return columns;
};
